import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { TestModuleAutomationFramework } from '../../../../models/test-module.model';

interface FrameworkOption {
  value: TestModuleAutomationFramework;
  labelKey: string;
}

function noop(): void {
  return;
}

@Component({
  selector: 'app-test-module-automation-card',
  imports: [TranslatePipe, MatIcon, ReactiveFormsModule, MatFormField, MatLabel, MatSelect, MatOption],
  templateUrl: './test-module-automation-card.component.html',
  styleUrl: './test-module-automation-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestModuleAutomationCardComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly frameworkControl = input.required<FormControl<TestModuleAutomationFramework | null>>();
  readonly automationKey = input<string | null>(null);
  readonly readOnly = input(false);

  readonly copied = signal(false);

  private copyResetHandle: ReturnType<typeof setTimeout> | null = null;

  readonly frameworkOptions: readonly FrameworkOption[] = [
    { value: 'CYPRESS', labelKey: 'modules.form.automation.framework.cypress' },
    { value: 'PLAYWRIGHT', labelKey: 'modules.form.automation.framework.playwright' },
    { value: 'POSTMAN_NEWMAN', labelKey: 'modules.form.automation.framework.postmanNewman' },
    { value: 'ROBOT_FRAMEWORK', labelKey: 'modules.form.automation.framework.robotFramework' },
    { value: 'SELENIUM', labelKey: 'modules.form.automation.framework.selenium' },
  ];

  constructor() {
    effect(() => {
      if (this.readOnly()) this.frameworkControl().disable({ emitEvent: false });
    });
    this.destroyRef.onDestroy(() => this.clearCopyReset());
  }

  copyKey(): void {
    const key = this.automationKey();
    if (!key || !navigator.clipboard) return;
    navigator.clipboard.writeText(key).then(() => this.flagCopied(), noop);
  }

  private flagCopied(): void {
    this.copied.set(true);
    this.clearCopyReset();
    this.copyResetHandle = setTimeout(() => this.copied.set(false), 1400);
  }

  private clearCopyReset(): void {
    if (this.copyResetHandle !== null) {
      clearTimeout(this.copyResetHandle);
      this.copyResetHandle = null;
    }
  }
}
